import { useContext, useEffect, useState } from 'react';
import PieChart from './PieChart';
import { useKpiCollection } from '../hooks/useKpiCollection';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import ListeningView from './ListeningView';
import { ThemesKpiPoint, ThemeValuePoint } from '@configTypes/kpiThemes';
import { useGlobalState } from '../providers/globalState';

type Theme = {
  theme: string;
  percentage: number;
};

export function ThemesView() {
  const globalState = useGlobalState();
  const { dispatch } = globalState ?? {};
  const klartextEventConfig = useContext(KlartextEventConfigContext);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [themesKpi] = useKpiCollection<ThemesKpiPoint[]>({
    eventId: klartextEventConfig?.eventId ?? null,
    kpiName: 'Themes',
    initialState: [] as ThemesKpiPoint[],
  });
  const getThemePercentages = (arr: ThemeValuePoint[]): Theme[] => {
    const totalStatmentsCount = arr.reduce((acc, value) => acc + value.statements.length, 0);
    const percentages = arr.map(value => ({
      theme: value.theme,
      percentage: (value.statements.length / totalStatmentsCount) * 100,
    }));

    return percentages as Theme[];
  };

  useEffect(() => {
    if (themesKpi.length === 0) return;

    //combine all values from all kpi points, and remove duplicates
    const allThemes = themesKpi.reduce((acc, value) => {
      return [...acc, ...value.value];
    }, [] as ThemeValuePoint[]);
    const uniqueThemes = Array.from(new Set(allThemes.map(theme => theme.theme))).map(theme => {
      return allThemes.find(t => t.theme === theme) as ThemeValuePoint;
    });
    setThemes(getThemePercentages(uniqueThemes));
  }, [themesKpi]);

  useEffect(() => {
    if (themes.length === 0) return;
    // get the theme with the highest percentage
    const mainTheme = themes.reduce((acc, theme) => (theme.percentage > acc.percentage ? theme : acc), themes[0]);
    dispatch?.({ type: 'SET_MAIN_THEME', payload: mainTheme.theme ?? '' });
  }, [themes]);

  if (!themes || themes.length === 0) {
    return <ListeningView />;
  }

  return (
    <div className="h-full w-full">
      <PieChart
        labels={themes.map(t => t.theme.toString())}
        data={themes.map(t => t.percentage)}
        dataDescriptor={'% of views in category'}
      />
    </div>
  );
}
